import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { candidateApi } from "@/lib/candidateApi";
import { useInvite } from "@/hooks/useInvite";
import { CandidateFlow } from "@/shell/CandidateFlow";

// Prep loading (Jinja: prep_loading.html). Polls prep-status every 2s, advances a
// progress bar + 4 step rows, and redirects to the live session once status=ready.
const STEPS = [
  "Reading your CV and job description",
  "Personalising interview questions",
  "Calibrating interviewer persona",
  "Final checks",
];
const MAX_POLLS = 90; // ~180s

type Phase = "loading" | "ready" | "failed" | "expired" | "timeout";

function StepRow({ label, state }: { label: string; state: "pending" | "active" | "done" }) {
  return (
    <div className="flex items-center gap-3">
      <span
        className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 ${
          state === "done"
            ? "border-good bg-good text-white"
            : state === "active"
              ? "border-accent"
              : "border-slate-200 bg-paper"
        }`}
      >
        {state === "done" && (
          <svg width="9" height="9" viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <path d="M5 10.5l3 3 7-7" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
        {state === "active" && <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-accent" />}
      </span>
      <span className={`text-sm ${state === "done" ? "text-good" : state === "active" ? "font-medium text-ink" : "text-slate-500"}`}>
        {label}
      </span>
    </div>
  );
}

export function PrepLoading() {
  const { token, sessionId } = useParams<{ token: string; sessionId: string }>();
  const navigate = useNavigate();
  const { data: invite } = useInvite(token);

  const [progress, setProgress] = useState(8);
  const [stage, setStage] = useState(0); // 0..3 active step index
  const [phase, setPhase] = useState<Phase>("loading");
  const polls = useRef(0);

  useEffect(() => {
    if (!sessionId) return;
    let live = true;
    let timer: ReturnType<typeof setTimeout>;

    async function tick() {
      if (!live || !sessionId) return;
      polls.current += 1;
      const r = await candidateApi.prepStatus(sessionId);
      if (!live) return;

      if (!r.ok) {
        if (r.status === 404) { setPhase("expired"); return; }
        // transient error — keep trying
      } else {
        const { status, researchDone, questionsDone } = r;
        if (status === "ready" || status === "active") {
          setProgress(100);
          setStage(3);
          setPhase("ready");
          setTimeout(() => live && navigate(`/invite/${token}/session/${sessionId}`), 600);
          return;
        }
        if (status === "prep_failed" || status === "failed") { setPhase("failed"); return; }
        // advance stage from the two prep signals
        const s = !researchDone ? 0 : !questionsDone ? 1 : 2;
        setStage(s);
        setProgress((p) => Math.min(90, p + (s + 1) * 6));
      }

      if (polls.current >= MAX_POLLS) { setPhase("timeout"); return; }
      timer = setTimeout(tick, 2000);
    }

    timer = setTimeout(tick, 1500);
    return () => { live = false; clearTimeout(timer); };
  }, [sessionId, token, navigate]);

  const statusMsg =
    phase === "ready" ? "Ready!"
    : stage === 0 ? "Researching the role…"
    : stage === 1 ? "Building your question bank…"
    : "Final checks…";

  return (
    <CandidateFlow agencyName={invite?.agencyName} logoUrl={invite?.branding?.logoUrlLight || undefined} logoFilter={invite?.branding?.logoFilter}>
      <div className="mx-auto max-w-sm text-center">
        <span className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-accent-50 text-accent">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <circle cx="12" cy="13" r="8" stroke="currentColor" strokeWidth="2" />
            <path d="M12 9v4l2.5 2M9 2h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </span>

        <h1 className="text-xl font-semibold text-ink">Preparing your session</h1>
        <p className="mt-1 text-sm text-slate-500">
          {invite?.candidateName ? `${invite.candidateName} · ` : ""}
          {invite?.role} {invite?.company ? `at ${invite.company}` : ""}
        </p>
        <p className="mt-1 text-xs text-slate-400">This takes about 90 seconds</p>

        <div className="mt-6 h-1 w-full overflow-hidden rounded-full bg-paper-3">
          <div
            className={`h-full rounded-full transition-[width] duration-700 ${phase === "failed" || phase === "expired" ? "bg-crit" : "bg-accent"}`}
            style={{ width: `${phase === "failed" || phase === "expired" ? 100 : progress}%` }}
          />
        </div>

        <div className="mt-4 min-h-[18px] text-[13px] text-slate-500">
          {phase === "failed" ? (
            <span>
              Preparation failed.{" "}
              <Link to={`/invite/${token}/setup`} className="text-accent underline">Tap here to retry.</Link>
            </span>
          ) : phase === "expired" ? (
            <span>
              Session expired.{" "}
              <Link to={`/invite/${token}`} className="text-accent underline">Return to your invite link</Link>{" "}
              to start again.
            </span>
          ) : phase === "timeout" ? (
            <span>
              Taking longer than expected.{" "}
              <Link to={`/invite/${token}/setup`} className="text-accent underline">Go back to setup</Link>{" "}
              and try again.
            </span>
          ) : (
            statusMsg
          )}
        </div>

        <div className="mx-auto mt-6 flex max-w-xs flex-col gap-2.5 text-left">
          {STEPS.map((label, i) => (
            <StepRow
              key={label}
              label={label}
              state={phase === "ready" || i < stage ? "done" : i === stage ? "active" : "pending"}
            />
          ))}
        </div>
      </div>
    </CandidateFlow>
  );
}
